import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';

const formatCurrency = (val) => Number(val || 0).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function MyTenants() {
  const { user } = useContext(AuthContext);
  const [tenants, setTenants] = useState([]);
  const [archivedTenants, setArchivedTenants] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const archivedCount = archivedTenants.length;
  const [lastViewedAt, setLastViewedAt] = useState(null);
  const [tenantsUnread, setTenantsUnread] = useState(0);
  const [hideNewItems, setHideNewItems] = useState(localStorage.getItem('dismiss_newitems_landlord_tenants') === '1');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [properties, setProperties] = useState([]);
  const [inviteStep, setInviteStep] = useState('form');
  const [inviteForm, setInviteForm] = useState({
    tenant_email: '',
    tenant_name: '',
    tenant_phone: '',
    property_id: '',
    unit_label: '',
    monthly_rent: '',
    rent_due_day: 1,
    lease_start_date: '',
    lease_end_date: '',
    role: 'primary',
    message: ''
  });

  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteExpanded, setInviteExpanded] = useState(false);

  useEffect(() => {
    const fetchTenants = async () => {
      if (!user || !user.token) {
        setError('Please log in to view your tenants.');
        setLoading(false);
        return;
      }

      try {
        const [countRes, response, archivedRes, propertiesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/tenants/unread-count`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          fetch(`${API_BASE_URL}/api/landlords/active-tenants`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          fetch(`${API_BASE_URL}/api/landlords/active-tenants?archived=1`, {
            headers: { Authorization: `Bearer ${user.token}` }
          }),
          fetch(`${API_BASE_URL}/api/landlords/properties`, {
            headers: { Authorization: `Bearer ${user.token}` }
          })
        ]);

        if (countRes.ok) {
          const countData = await countRes.json().catch(() => ({}));
          setTenantsUnread(countData.count || 0);
          setLastViewedAt(countData.last_viewed_at || null);
        }

        if (!response.ok) {
          throw new Error('Failed to fetch active tenants.');
        }

        const data = await response.json();
        const archivedData = archivedRes.ok ? await archivedRes.json().catch(() => ([])) : [];
        const propertiesData = propertiesRes.ok ? await propertiesRes.json().catch(() => ([])) : [];
        const propertyList = Array.isArray(propertiesData) ? propertiesData : (propertiesData.properties || []);
        setTenants(Array.isArray(data) ? data : []);
        setArchivedTenants(Array.isArray(archivedData) ? archivedData : []);
        setProperties(Array.isArray(propertyList) ? propertyList : []);

        if (user?.token) {
          await fetch(`${API_BASE_URL}/api/tenants/mark-viewed`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${user.token}` }
          });
          window.dispatchEvent(new Event('tenants:viewed'));
        }
      } catch (err) {
        setError(err.message || 'Something went wrong.');
      } finally {
        setLoading(false);
      }
    };

    fetchTenants();
  }, [user]);

  const latestTenants = [...tenants]
    .sort((a, b) => new Date(b.created_at || b.lease_start_date || 0) - new Date(a.created_at || a.lease_start_date || 0))
    .slice(0, 3);

  const handleArchiveTenant = async (tenantId) => {
    if (!user?.token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/landlords/active-tenants/${tenantId}/archive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (!res.ok) throw new Error('Failed to archive tenant.');
      const { tenant } = await res.json().catch(() => ({}));
      if (tenant?.id) {
        setTenants((prev) => prev.filter((t) => t.id !== tenant.id));
        const archivedItem = tenants.find((t) => t.id === tenant.id);
        if (archivedItem) {
          setArchivedTenants((prev) => [{ ...archivedItem, status: tenant.status }, ...prev]);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to archive tenant.');
    }
  };

  const handleRestoreTenant = async (tenantId) => {
    if (!user?.token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/landlords/active-tenants/${tenantId}/unarchive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${user.token}` }
      });
      if (!res.ok) throw new Error('Failed to restore tenant.');
      const { tenant } = await res.json().catch(() => ({}));
      if (tenant?.id) {
        setArchivedTenants((prev) => prev.filter((t) => t.id !== tenant.id));
        const restoredItem = archivedTenants.find((t) => t.id === tenant.id);
        if (restoredItem) {
          setTenants((prev) => [{ ...restoredItem, status: tenant.status }, ...prev]);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to restore tenant.');
    }
  };

  const resetInviteForm = () => {
    setInviteForm({
      tenant_email: '',
      tenant_name: '',
      tenant_phone: '',
      property_id: '',
      unit_label: '',
      monthly_rent: '',
      rent_due_day: 1,
      lease_start_date: '',
      lease_end_date: '',
      role: 'primary',
      message: '',
      _rentFromProperty: false
    });
    setInviteStep('form');
  };

  const handleInviteContinue = (e) => {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');
    if (!inviteForm.tenant_email || !inviteForm.property_id || !inviteForm.monthly_rent || !inviteForm.lease_start_date) {
      setInviteError('Please fill in the required fields.');
      return;
    }
    setInviteStep('review');
  };

  const handleInviteSend = async () => {
    if (!user?.token) return;
    setInviteSending(true);
    setInviteError('');
    setInviteSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/landlords/tenant-invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${user.token}` },
        body: JSON.stringify({
          ...inviteForm,
          rent_due_day: Number(inviteForm.rent_due_day) || 1,
          monthly_rent: Number(inviteForm.monthly_rent)
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to send invite.');
      setInviteSuccess(`✅ Invite sent to ${inviteForm.tenant_email}! They'll receive an email with a link to accept.`);
      resetInviteForm();
      setInviteExpanded(false);
    } catch (err) {
      setInviteError(err.message || 'Unable to send invite.');
    } finally {
      setInviteSending(false);
    }
  };

  return (
    <main className="App-main tenants-page">
      <section className="ledger-header has-actions">
        <div>
          <p className="eyebrow-tag">Relationship hub</p>
          <h2>My Tenants {tenantsUnread > 0 && <span className="nav-badge">{tenantsUnread}</span>}</h2>
          <p>Track active leases, next rent dates, and communication in one place.</p>
        </div>
      </section>


      {!hideNewItems && tenantsUnread > 0 && latestTenants.length > 0 && (
        <div className="dashboard-section new-items-card" style={{ borderLeft: '4px solid #2f6fed', background: '#f5f8ff' }}>
          <div className="section-header">
            <h3>New items</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="report-chip report-chip--muted">Latest tenants</span>
              <button
                className="card-dismiss"
                onClick={() => { localStorage.setItem('dismiss_newitems_landlord_tenants', '1'); setHideNewItems(true); }}
                aria-label="Dismiss"
              >
                Dismiss
              </button>
            </div>
          </div>
        {latestTenants.length === 0 ? (
          <p className="status-text">No tenants yet.</p>
        ) : (
          <div className="inquiry-list">
            {latestTenants.map((tenant) => {
              const createdAt = tenant.created_at || tenant.lease_start_date || null;
              const isNew = lastViewedAt && createdAt && new Date(createdAt) > new Date(lastViewedAt);
              return (
                <div key={`latest-${tenant.id || tenant.application_id}`} className="inquiry-card">
                  <p><strong>{tenant.tenant_name || tenant.full_name || 'Tenant'}</strong></p>
                  <p className="status-text">{tenant.property_address || tenant.address || 'Property on file'}{tenant.unit_label ? ` — Unit ${tenant.unit_label}` : ''}</p>
                  <p className="timestamp">{createdAt ? new Date(createdAt).toLocaleDateString() : '—'}</p>
                  <div className="form-actions" style={{ marginTop: '0.5rem' }}>
                    <Link className="secondary-button" to={`/landlord/my-tenants/${tenant.id || tenant.application_id}`}>View details</Link>
                    {isNew && <span className="report-chip" style={{ marginLeft: '0.5rem' }}>New</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {loading && <p className="status-text">Loading active tenants...</p>}
      {error && !loading && <p className="status-text error-text">{error}</p>}

      {!loading && !error && !showArchived && tenants.length === 0 && (
        <div className="empty-state">
          <h3>No active tenants yet</h3>
          <p>Approved applicants will appear here once leases are active.</p>
          <Link className="primary-button" to="/landlord/applicants">Review Applications</Link>
        </div>
      )}

      {!loading && !error && showArchived && archivedTenants.length === 0 && (
        <div className="empty-state">
          <h3>No archived tenants</h3>
          <p>Archived tenants will show here once you archive them.</p>
        </div>
      )}

      {!showArchived && (
        <div className="tenants-grid">
          {tenants.map((tenant) => (
            <article key={tenant.id || tenant.application_id} className="tenant-card">
              <div className="tenant-card-header">
                <h3>{tenant.tenant_name || tenant.full_name || 'Tenant'}</h3>
                <span className={`status-pill ${tenant.status || 'active'}`}>{tenant.status || 'active'}</span>
              </div>
              <p className="tenant-property">{tenant.property_address || tenant.address || 'Property on file'}{tenant.unit_label ? ` — Unit ${tenant.unit_label}` : ''}</p>
              <div className="tenant-meta">
                <div>
                  <span>Monthly Rent</span>
                  <strong>${tenant.monthly_rent || tenant.rent || '—'}</strong>
                </div>
                <div>
                  <span>Next Due</span>
                  <strong>{tenant.next_due_date || tenant.rent_due_date || '—'}</strong>
                </div>
              </div>
              <div className="tenant-actions">
                <Link
                  className="ghost-button"
                  to={tenant.application_id ? `/landlord/messages?application_id=${tenant.application_id}` : '/landlord/messages'}
                >
                  Send Message
                </Link>
                {tenant.status && tenant.status !== 'active' && tenant.status !== 'archived' && (
                  <button className="ghost-button" onClick={() => handleArchiveTenant(tenant.id)}>
                    Archive
                  </button>
                )}
                <Link className="primary-button" to={`/landlord/my-tenants/${tenant.id || tenant.application_id}`}>View Details</Link>
              </div>
            </article>
          ))}
        </div>
      )}

      {showArchived && (
        <div className="tenants-grid">
          {archivedTenants.map((tenant) => (
            <article key={`archived-${tenant.id || tenant.application_id}`} className="tenant-card">
              <div className="tenant-card-header">
                <h3>{tenant.tenant_name || tenant.full_name || 'Tenant'}</h3>
                <span className="status-pill archived">archived</span>
              </div>
              <p className="tenant-property">{tenant.property_address || tenant.address || 'Property on file'}{tenant.unit_label ? ` — Unit ${tenant.unit_label}` : ''}</p>
              <div className="tenant-meta">
                <div>
                  <span>Monthly Rent</span>
                  <strong>${tenant.monthly_rent || tenant.rent || '—'}</strong>
                </div>
                <div>
                  <span>Next Due</span>
                  <strong>{tenant.next_due_date || tenant.rent_due_date || '—'}</strong>
                </div>
              </div>
              <div className="tenant-actions">
                <button className="ghost-button" onClick={() => handleRestoreTenant(tenant.id)}>
                  Restore
                </button>
                <Link className="primary-button" to={`/landlord/my-tenants/${tenant.id || tenant.application_id}`}>View Details</Link>
              </div>
            </article>
          ))}
        </div>
      )}

      <section className="dashboard-section" style={{ marginTop: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="secondary-button"
            onClick={() => setInviteExpanded((prev) => !prev)}
            aria-expanded={inviteExpanded}
            aria-controls="existing-tenant-invite"
          >
            {inviteExpanded ? 'Hide invite form' : 'Invite existing tenant'}
          </button>
          <p className="status-text" style={{ margin: 0, fontSize: '0.82rem' }}>
            Already have a tenant? Send them an invite to connect their account, set up their lease, and start collecting rent online.
          </p>
        </div>

        {inviteSuccess && (
          <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', borderRadius: '10px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' }}>
            <p style={{ margin: 0, fontSize: '0.88rem', color: 'var(--text-color)' }}>{inviteSuccess}</p>
          </div>
        )}

        {inviteExpanded && (
          <div id="existing-tenant-invite" style={{ marginTop: '1rem' }}>
            {inviteError && <p className="error-message">{inviteError}</p>}

            {inviteStep === 'form' && (
              <form className="form-grid" onSubmit={handleInviteContinue} style={{ marginTop: '0.75rem' }}>
                <div className="form-group">
                  <label>Property *</label>
                  <select
                    className="form-control"
                    value={inviteForm.property_id}
                    onChange={(e) => {
                      const pid = e.target.value;
                      const prop = properties.find(p => String(p.id) === String(pid));
                      setInviteForm((prev) => ({
                        ...prev,
                        property_id: pid,
                        monthly_rent: prop?.rent || prop?.rent_amount || prev.monthly_rent || '',
                        rent_due_day: prop?.rent_due_day || prev.rent_due_day || 1,
                        unit_label: prop?.unit_label || prop?.unit || prev.unit_label || '',
                        _rentFromProperty: !!(prop?.rent || prop?.rent_amount)
                      }));
                    }}
                    required
                  >
                    <option value="">Select property</option>
                    {properties.map((property) => (
                      <option key={property.id} value={property.id}>{property.address}{property.unit ? ` — Unit ${property.unit}` : ''}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Tenant email *</label>
                  <input
                    className="form-control"
                    type="email"
                    value={inviteForm.tenant_email}
                    onChange={(e) => setInviteForm((prev) => ({ ...prev, tenant_email: e.target.value }))}
                    required
                    placeholder="tenant@email.com"
                  />
                </div>
                <div className="form-group">
                  <label>Lease start date *</label>
                  <input
                    className="form-control"
                    type="date"
                    value={inviteForm.lease_start_date}
                    onChange={(e) => setInviteForm((prev) => ({ ...prev, lease_start_date: e.target.value }))}
                    required
                  />
                </div>

                {inviteForm.property_id && (
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <p className="status-text" style={{ margin: 0 }}>
                      Rent: <strong>{inviteForm.monthly_rent ? formatCurrency(inviteForm.monthly_rent) : 'Not set'}</strong> · Due day: <strong>{inviteForm.rent_due_day || 1}</strong>
                      {!inviteForm.monthly_rent && <span style={{ color: 'var(--error-color)', marginLeft: '0.5rem', fontSize: '0.8rem' }}>Set rent in property settings first</span>}
                    </p>
                  </div>
                )}

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Message (optional)</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    placeholder="Add a personal note to the invite email"
                    value={inviteForm.message}
                    onChange={(e) => setInviteForm((prev) => ({ ...prev, message: e.target.value }))}
                  />
                </div>

                <div className="form-actions" style={{ gridColumn: '1 / -1' }}>
                  <button className="primary-button" type="submit" disabled={!inviteForm.monthly_rent}>Review invite</button>
                </div>
              </form>
            )}

            {inviteStep === 'review' && (
              <div style={{ marginTop: '0.75rem' }}>
                <div className="dashboard-section" style={{ background: '#f8fafc' }}>
                  <p className="status-text"><strong>Tenant:</strong> {inviteForm.tenant_name || inviteForm.tenant_email}</p>
                  <p className="status-text"><strong>Email:</strong> {inviteForm.tenant_email}</p>
                  <p className="status-text"><strong>Property:</strong> {properties.find((p) => String(p.id) === String(inviteForm.property_id))?.address || '—'}</p>
                  {inviteForm.unit_label && <p className="status-text"><strong>Unit:</strong> {inviteForm.unit_label}</p>}
                  <p className="status-text"><strong>Rent:</strong> {formatCurrency(inviteForm.monthly_rent || 0)} due on day {inviteForm.rent_due_day}</p>
                  <p className="status-text"><strong>Lease:</strong> {inviteForm.lease_start_date || '—'} {inviteForm.lease_end_date ? `→ ${inviteForm.lease_end_date}` : ''}</p>
                  {inviteForm.message && <p className="status-text"><strong>Message:</strong> {inviteForm.message}</p>}
                </div>
                <div className="form-actions" style={{ marginTop: '0.75rem' }}>
                  <button className="secondary-button" onClick={() => setInviteStep('form')}>Back</button>
                  <button className="primary-button" onClick={handleInviteSend} disabled={inviteSending}>
                    {inviteSending ? 'Sending…' : 'Send invite'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <div className="form-actions" style={{ marginTop: '1.5rem' }}>
        <button className="secondary-button" onClick={() => setShowArchived((prev) => !prev)}>
          {showArchived ? 'Hide archived' : `View archived${archivedCount ? ` (${archivedCount})` : ''}`}
        </button>
      </div>
    </main>
  );
}

export default MyTenants;
